import ChatProposalStatus from "../../enum/chat/ChatProposalStatus";
import EntityType from "../../enum/EntityType";
import AnyColor from "../AnyColor";
import CustomField from "../customField/CustomField";
import { CustomFieldValueType } from "../customField/CustomFieldValueType";
import { AnyEntityId, Deal, EntityTypesSupportedByEmailFeature } from "../entity";
import DealLossReason from "../entity/deals/DealLossReason";
import Funnel from "../entity/deals/Funnel";
import Stage from "../entity/deals/Stage";
import IField from "../fieldModel/IField";

import type { ChatContextEntityType } from "./ChatMessage";

export interface ChatBriefContent {
  bullets: string[];
  narrative: string;
}

export interface ChatRecordCardContent {
  summary: string;
}

// The call card's payload, closed to three fields on the platform side. `phoneNumber` is the
// stored value verbatim — the only guarantee is that it contains one digit, so it is never
// reformatted or truncated on the way to the card. `companyName` is a bare name with no id: a
// subtitle, not a second record, and present only for a contact that has a company.
export interface ChatCallCardContent {
  companyName?: string;
  displayName: string;
  phoneNumber: string;
}

// One page of a record list. Every field but `id` is optional: the server omits empty values
// rather than sending null, so an absent phone is a missing key.
interface ChatListRecordBase {
  // Distance from the rep, populated only when the agent sorted by distance — a "near me" ask
  // alone does not fill it. Carried, not rendered.
  distance?: number;
  id: number;
  name?: string;
}

export interface ChatCompanyListRecord extends ChatListRecordBase {
  email?: string;
  phone?: string;
}

export interface ChatPersonListRecord extends ChatListRecordBase {
  // The parent company, with its id — the person row links to it, so a bare name would not do.
  account?: { id: number; name?: string };
  email?: string;
  phone?: string;
}

export interface ChatActivityListRecord extends ChatListRecordBase {
  completed?: boolean;
  // color and name are not optional inside this object: ActivityTypeIcon's own prop type is
  // ActivityTypeRef, which requires both, so a half-formed type object is dropped in the parser
  // rather than passed on for the icon to fail on.
  crmActivityType?: { color: AnyColor | null; id: number; name: string };
  startAt?: string;
}

export interface ChatDealListRecord extends ChatListRecordBase {
  amount?: number;
  closingDate?: string;
  funnel?: { id: number; name?: string };
  // `color: AnyColor | null` rather than an optional string, so the object satisfies EntityBadge's
  // `Colored & Named` without a cast at the call site.
  stage?: { color: AnyColor | null; id?: number; name?: string; type?: string };
  user?: { fullName?: string; id?: number };
}

// Discriminated on entityType, so a row's shape is decided once by the card that renders it
// rather than re-checked field by field at every use.
export type ChatRecordListContent =
  | { entityType: EntityType.ACTIVITY; records: ChatActivityListRecord[]; total: number }
  | { entityType: EntityType.COMPANY; records: ChatCompanyListRecord[]; total: number }
  | { entityType: EntityType.DEAL; records: ChatDealListRecord[]; total: number }
  | { entityType: EntityType.PERSON; records: ChatPersonListRecord[]; total: number };

// FE-only sentinel, mirroring ChatMessageContentType.UNSUPPORTED: an action type we don't
// know, or a known type whose payload failed shape validation. rawType keeps what the server
// actually sent, for logging.
export const UNKNOWN_PROPOSAL_ACTION = "unknown" as const;

export interface ChatUnknownProposalAction {
  rawType: string;
  type: typeof UNKNOWN_PROPOSAL_ACTION;
}

// A custom-field entry exactly as it travels, in BOTH directions: the platform sends this shape
// inside a proposal, and accepts it back in finalValues.customFields. The web app's own
// ChatCustomFieldValue is the narrower thing it works with in between — see
// apps/web-app/src/types/chat/ChatCustomFieldValue.ts.
export interface ChatCustomFieldValueRaw {
  customFieldId: CustomField["id"];
  // `cf.<esKey>`, matching the definition's own key. Never read on the platform's write path — it
  // resolves the field by customFieldId — but it is the key the web app itself works by.
  esKey: CustomField["esKey"];
  // The platform's stored form, which differs by data type — a bare number for single-option, an
  // array for multi-option, a one-element array for everything else. The field agent always sends
  // the array variants, but this is the shape the rest of the app reads a custom-field value by.
  //
  // undefined is excluded rather than left representable: an entry the wire sends always carries a
  // value, so the invariant is enforced here instead of documented — and it matches currentValue on
  // the record_update entry below, which already excludes it.
  value: Exclude<CustomFieldValueType, undefined>;
}

// The three entities a proposal can hang off. Its own type rather than ChatContextEntityType, which
// admits Activities — legal as a message context, never as a parent record or a record_update target.
export type ChatRecordParentEntityType = EntityType.COMPANY | EntityType.DEAL | EntityType.PERSON;

export interface ChatRecordUpdateFieldChange {
  // The record's raw stored attribute, and NOT the same type as proposedValue: BE snapshots the
  // column while parsing the proposal, so a Deal's closingDate arrives here as a full timestamptz
  // string and there as a bare "YYYY-MM-DD".
  currentValue: unknown;
  // The platform WRITE column, which is not always the agent's name for the field — a Deal's loss
  // comment travels as dealLossComment. IField's own name for that concept.
  field: IField["platformName"];
  proposedValue: unknown;
}

// customFieldId resolves a CustomField on its own: FieldModel dual-keys custom fields under esKey
// and under String(id), so the definition, its label and its option value→name map all come from
// the field model rather than from a second lookup.
export interface ChatRecordUpdateCustomFieldChange extends ChatCustomFieldValueRaw {
  // null means the record had no value, which is distinct from [] — [] as a proposed value is an
  // instruction to clear. undefined is excluded rather than merged into null: the parser normalises
  // an absent key, so every renderer has one emptiness check to make instead of two.
  currentValue: Exclude<CustomFieldValueType, undefined> | null;
}

// Editing fields on a Company, Person or Deal. The rep may edit the proposed values before
// confirming, but may never add a standard field the agent did not propose — the apply endpoint
// iterates the stored proposal, so an extra key is discarded and still reports `applied`.
export interface ChatRecordUpdateAction {
  // Empty on a custom-field-only edit, which the platform unit-tests: a case to render, not a
  // malformed payload.
  changes: ChatRecordUpdateFieldChange[];
  // Absent, never [], when the edit touches no custom field.
  customFields?: ChatRecordUpdateCustomFieldChange[];
  displayName: string;
  entityId: AnyEntityId;
  entityType: ChatRecordParentEntityType;
  type: "record_update";
}

// Logging a note on a record. body is editable by the rep before confirming
// (finalValues.note). entityId is apply-call-only — never shown to the rep.
export interface ChatLogNoteAction {
  body: string;
  displayName: string;
  // Bare `number`, not an entity id type: which entity it belongs to is decided by `entityType`,
  // so no single Entity["id"] fits.
  entityId: number;
  entityType: ChatContextEntityType;
  type: "log_note";
}

// One recipient as the platform hydrated it at propose time. Names and addresses ride along, so the
// card renders them without a fetch. Declared here rather than aliased onto a CRM entity shape: this
// is a wire contract, and `email` is a non-null string the parser guarantees.
export interface ChatSendEmailRecipient {
  email: string;
  id: number;
  name: string;
}

// The email content and recipients for a send_email proposal.
export interface ChatSendEmailPayload {
  body: string;
  recipientEntityType: EntityTypesSupportedByEmailFeature;
  recipientIds: number[];
  recipients: ChatSendEmailRecipient[];
  // Absent means "send on approval". An empty string in finalValues clears a proposed schedule;
  // omitting the key keeps it. Two different instructions.
  sendAt?: string;
  subject: string;
}

// Sending an email the agent drafted. The rep can edit subject, body and sendAt before confirming
// (finalValues); recipients are read-only in this release. entityId / entityType / displayName
// describe the record the email is ABOUT — which may be a Deal — never the address. The address is
// recipientEntityType plus recipients, and it is limited to Companies or People.
export interface ChatSendEmailAction {
  displayName: string;
  email: ChatSendEmailPayload;
  entityId: number;
  entityType: ChatContextEntityType;
  type: "send_email";
}

// The four relationships the agent can change. Every one is anchored on a Company, so a
// Person- or Deal-anchored request arrives phrased from the Company's side.
export type ChatRelationshipKey =
  | "account.contacts"
  | "account.deals"
  | "account.parentAccount"
  | "account.primaryContact";

export type ChatRelationshipOperation = "link" | "unlink";

// Narrower than ChatContextEntityType, which also admits an Activity: the record on the other end
// of a relationship is only ever one of these three.
export type ChatRelationshipEntityType = EntityType.COMPANY | EntityType.DEAL | EntityType.PERSON;

// A record on one end of a relationship. `name` can be "" when the server could not resolve it.
export interface ChatRelationshipEntityRef {
  entityType: ChatRelationshipEntityType;
  id: AnyEntityId;
  name: string;
}

// Linking or unlinking one of a Company's four relationships. The rep may swap the related record
// and, for an account.contacts link only, mark the anchor as that Person's primary company.
//
// Two shapes to know before rendering a diff. For account.contacts, previousEntity is always
// absent, because a set has no outgoing value. For account.deals, previousEntity is the COMPANY
// that currently holds the Deal, not another Deal — so it belongs on its own line, never opposite
// relatedEntity.
export interface ChatManageRelationshipAction {
  displayName: string;
  entityId: AnyEntityId;
  entityType: ChatContextEntityType;
  operation: ChatRelationshipOperation;
  previousEntity?: ChatRelationshipEntityRef;
  relatedEntity?: ChatRelationshipEntityRef;
  relationshipKey: ChatRelationshipKey;
  // Honored by the platform only on an account.contacts link. The rep can turn it off again: the
  // apply channel treats primary as three-state, so `false` is an instruction and not a no-op.
  setPrimary?: boolean;
  type: "manage_relationship";
}

// Discriminated on `type`. Later stories add one member per action type
// alongside their row renderer.
// `name` is "" when the server could not resolve the stage — see
// mmc-server change-deal-stage/handler.ts:223-228. The wire's own `type` is not modelled: nothing
// here reads it, since every won/lost decision uses the store-resolved Stage.
export type ChatDealStageRef = Pick<Stage, "id" | "name">;

// Moving a Deal to another stage in the same funnel. The rep may redirect the destination and,
// for a lost move, supply the loss reason and comment.
export interface ChatChangeDealStageAction {
  currentStage: ChatDealStageRef;
  displayName: string;
  entityId: Deal["id"];
  entityType: ChatContextEntityType;
  funnelId: Funnel["id"];
  lossComment?: string;
  lossReason?: Pick<DealLossReason, "id" | "name">;
  type: "change_deal_stage";
  updatedStage: ChatDealStageRef;
}

export interface ChatLogActivityAction {
  // Parent fields are OPTIONAL and travel together — all three are absent for a standalone
  // activity, which is the one ACTION_PROPOSAL whose message carries no context entity either.
  displayName?: string;
  entityId?: number;
  entityType?: ChatRecordParentEntityType;
  record: {
    // Display-only. The apply endpoint ignores it in finalValues: flipping log↔schedule needs a
    // fresh proposal, so nothing in the UI may offer to change it.
    completed: boolean;
    crmActivityType: { id: number; name: string };
    customFields?: ChatCustomFieldValueRaw[];
    // Optional like startAt, and editable like it: the apply endpoint keeps no key allowlist, so an
    // end time the platform does not yet write is inert rather than rejected.
    endAt?: string;
    name: string;
    note?: string;
    startAt?: string;
  };
  type: "log_activity";
}

// Same three members as ChatRecordParentEntityType and deliberately its own name: there the union
// means "the record this action hangs off", here it means "the record this action would create".
export type ChatCreatableEntityType = ChatRecordParentEntityType;

// Creating a Company, Person or Deal. Unlike every other action type, the platform writes
// finalValues key by key here instead of iterating the stored proposal, so unknown keys land.
export interface ChatCreateRecordAction {
  // Wire-shaped write columns, not display strings. Address arrives as SEVEN flat keys, not as
  // record_update's single compound object.
  proposedValues: Record<string, unknown>;
  // null when the agent had no signal to go on. Always paired with an empty proposedValues, so a
  // typeless card never has fields to discard when the rep picks a type.
  targetEntityType: ChatCreatableEntityType | null;
  type: "create_record";
}

export type ChatRouteStopOperation = "add" | "remove" | "replace";

// Narrower than ChatRecordParentEntityType, which admits a Deal: a route stop is only ever a
// Company or a Person. Address-only ("manual") stops carry no record at all — they appear in the
// snapshot below with null identity, and can never be a proposal's target.
export type ChatRouteStopEntityType = EntityType.COMPANY | EntityType.PERSON;

// A stop already on the route, named by its record. What a replace swaps out.
export interface ChatRouteStopTarget {
  displayName: string;
  entityId: number;
  entityType: ChatRouteStopEntityType;
}

// One stop in the propose-time route snapshot. Almost everything is nullable: an address-only stop
// has no record identity, an unpriced route has no arrival times, and a stop the rep already
// visited always has a null arrivalTime even on a fully priced route.
//
// `displayOrder` is NOT the persisted column — the platform overwrites it with the stop's 1-based
// index in this array, after hoisting visited stops to the front. Render in array order.
//
// `previousArrivalTime` is sent on a REMOVAL only, and then on every stop: one whose time did not
// move carries it equal to `arrivalTime` rather than absent.
export interface ChatRouteCardStop {
  arrivalTime: null | string;
  checkedOff: boolean;
  displayOrder: null | number;
  entityId: null | number;
  entityType: ChatRouteStopEntityType | null;
  name: null | string;
  previousArrivalTime?: null | string;
}

// "moved" is declared because the platform's own type declares it; nothing emits it today.
export type ChatRouteStopChangeKind = "added" | "moved" | "removed";

// Which stop the proposal changes. Identity can be null the same way a stop's can. `changeKind`
// has no renderer today — it is parsed only so a future consumer has it — so an unrecognised value
// is normalised to null rather than failing the whole snapshot the way a bad `entityType` does.
export interface ChatRouteCardAffectedStop {
  changeKind: ChatRouteStopChangeKind | null;
  entityId: null | number;
  entityType: ChatRouteStopEntityType | null;
  fromIndex?: null | number;
  previousArrivalTime?: null | string;
  toIndex?: null | number;
}

// The route as it would look after the change. Display-only, and optional — absent when the
// platform could not build a preview. `priced: false` means the routing provider was unavailable,
// so every arrivalTime is null and the order is the stored fallback, NOT a solved route.
export interface ChatRouteCardSnapshot {
  affected: ChatRouteCardAffectedStop;
  priced: boolean;
  startAt: null | string;
  stops: ChatRouteCardStop[];
}

// Editing the rep's own route. `operation` decides which optional fields are populated — key off
// it rather than inferring the shape from a field being present.
//
// Anchored on the STOP's record, never the route: a route is not a legal chat context. On a
// replace the anchor is the INCOMING record and the outgoing one rides in `replacedStop`, which is
// also the only place it appears — the platform drops it from `routeCard.stops` before pricing.
//
// `position` is a 0-based slot or null, present on an add only. It is display-only: the apply
// endpoint has no field to send an edited position back, and discards anything extra silently.
export interface ChatManageRouteStopsAction {
  displayName: string;
  entityId: number;
  entityType: ChatRouteStopEntityType;
  operation: ChatRouteStopOperation;
  position?: null | number;
  replacedStop?: ChatRouteStopTarget;
  routeCard?: ChatRouteCardSnapshot;
  routeId: number;
  routeName: string;
  type: "manage_route_stops";
}

// Discriminated on `type`. Later stories add one member per action type
// alongside their row renderer.
export type ChatProposalAction =
  | ChatChangeDealStageAction
  | ChatCreateRecordAction
  | ChatLogActivityAction
  | ChatLogNoteAction
  | ChatManageRelationshipAction
  | ChatManageRouteStopsAction
  | ChatRecordUpdateAction
  | ChatSendEmailAction
  | ChatUnknownProposalAction;

export interface ChatProposal {
  action: ChatProposalAction;
  expiresAt: string;
  proposalId: number;
  // Asserted by parseChatResponse rather than narrowed: BE pins these five with a DB CHECK
  // constraint. resolveProposalRowStatus() tests membership before rendering anyway, so a value that
  // somehow escaped that constraint shows as an unknown row rather than as a lie.
  status: ChatProposalStatus;
}
